import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import { parseISO, addDays, isAfter } from 'date-fns';

const state = {
  creator: {},
  campaign: {},
  campaignApplication: {},
  campaignApplicationStatuses: {
    pending_approval: 'pending_approval',
    rejected_approval: 'rejected_approval',
    pending_contract: 'pending_contract',
    negotiating: 'negotiating',
    waiting_for_content: 'waiting_for_content',
    review_content: 'review_content',
    publish_content: 'publish_content',
    pending_contract: 'pending_contract',
    pending_payment: 'pending_payment',
    completed: 'completed',
    cancelled: 'cancelled',
  },
  uploadedContent: [],
  order: {},
  campaignOrderGuidelines: '',
  photosWeWant: [],
  youtubeLink: '',
  isEligibleForCampaign: '',
  contentRejectionsCount: '',
  failedDeliverables: {
    instagramPosts: [],
  },
  postedDeliverables: {
    youtubeLink: '',
    instagramPosts: [],
    tikTokPosts: [],
    youtubePosts: [],
  },
  steps: {
    briefAndJoin: {
      id: 'briefAndJoin',
      name: 'Check the Story & Collaboration Terms',
      message: '',
      completed: false,
      open: false,
      visible: true,
      active: false,
    },
    placeOrder: {
      id: 'placeOrder',
      name: 'Place order',
      message: '',
      completed: false,
      open: false,
      visible: true,
      active: false,
    },
    uploadContent: {
      id: 'uploadContent',
      name: 'Upload Deliverables',
      message: '',
      completed: false,
      open: false,
      visible: true,
      active: false,
    },
    postContent: {
      id: 'postContent',
      name: 'Post on Social Media',
      message: '',
      completed: false,
      open: false,
      visible: true,
      active: false,
    },
    joinAnotherCampaign: {
      id: 'joinAnotherCampaign',
      name: 'Join another campaign',
      message: '',
      completed: false,
      open: false,
      visible: false,
      active: false,
    },
  },
};

const mutations = {
  setCampaign(state, campaign) {
    state.campaign = campaign;
  },
  setCreator(state, creator) {
    state.creator = creator;
  },
  setCampaignApplication(state, campaignApplication) {
    state.campaignApplication = campaignApplication;
  },
  setUploadedContent(state, uploadedContent) {
    state.uploadedContent = uploadedContent;
  },
  setOrder(state, order) {
    state.order = order;
  },
  setIsEligibleForCampaign(state, isEligibleForCampaign) {
    state.isEligibleForCampaign = isEligibleForCampaign;
  },
  setCampaignOrderGuidelines(state, campaignOrderGuidelines) {
    state.campaignOrderGuidelines = campaignOrderGuidelines;
  },
  setPhotosWeWant(state, photosWeWant) {
    state.photosWeWant = photosWeWant;
  },
  setYoutubeLink(state, youtubeLink) {
    state.youtubeLink = youtubeLink;
  },
  setPostedDeliverables(state, postedDeliverables) {
    state.postedDeliverables = postedDeliverables;
  },
  setFailedDeliverables(state, failedDeliverables) {
    state.failedDeliverables = failedDeliverables;
  },
  setInstagramPosts(state, instagramPosts) {
    state.postedDeliverables.instagramPosts = instagramPosts;
  },
  setTikTokPosts(state, tikTokPosts) {
    state.postedDeliverables.tikTokPosts = tikTokPosts;
  },
  setYoutubePosts(state, youtubePosts) {
    state.postedDeliverables.youtubePosts = youtubePosts;
  },
  toggleStep(state, id) {
    state.steps[id].open = !state.steps[id].open;
  },
  markStepHidden(state, id) {
    state.steps[id].visible = false;
  },
  markStepCompleted(state, id) {
    state.steps[id].completed = true;
    state.steps[id].open = false;
    state.steps[id].active = false;
  },
  markStepActive(state, id) {
    state.steps[id].active = true;
    state.steps[id].open = true;
  },
  addUploadedContent(state, name) {
    state.uploadedContent.push(name);
  },
  removeUploadedContent(state, file) {
    for (let i = 0; i < state.uploadedContent.length; i++) {
      if (state.uploadedContent[i].media_id === file.media_id) {
        state.uploadedContent.splice(i, 1);
      }
    }
  },
  setContentRejectionsCount(state, contentRejectionsCount) {
    state.contentRejectionsCount = contentRejectionsCount;
  },
};

const getters = {
  applied: (state) =>
    state.campaignApplication
      ? state.campaignApplication.status === state.campaignApplicationStatuses.pending_approval
      : false,
  approved: (state) =>
    state.campaignApplication
      ? ![
          state.campaignApplicationStatuses.pending_approval,
          state.campaignApplicationStatuses.rejected_approval,
          state.campaignApplicationStatuses.cancelled,
        ].includes(state.campaignApplication.status)
      : false,
  joined: (state) =>
    state.campaignApplication
      ? ![
          state.campaignApplicationStatuses.pending_approval,
          state.campaignApplicationStatuses.rejected_approval,
          state.campaignApplicationStatuses.pending_contract,
          state.campaignApplicationStatuses.negotiating,
        ].includes(state.campaignApplication.status)
      : false,
  instagramHandle: (state) => state.creator.instagram_handle,
  preApprovalCampaign(state) {
    if (state.campaign && state.campaign.options) return state.campaign.options.pre_approval;

    return false;
  },
  campaignApplyByPassed() {
    return isAfter(new Date(), addDays(parseISO(state.campaign.apply_by), 1));
  },
  reelsRequired: (state) => (state.campaign.deliverables.reels.enabled ? state.campaign.deliverables.reels.count : 0),
  storiesRequired: (state) =>
    state.campaign.deliverables.instagram_story.enabled ? state.campaign.deliverables.instagram_story.count : 0,
  postsRequired: (state) =>
    state.campaign.deliverables.instagram_post.enabled ? state.campaign.deliverables.instagram_post.count : 0,
  youtubeShortsRequired: (state) =>
    state.campaign.deliverables.youtube_shorts.enabled ? state.campaign.deliverables.youtube_shorts.count : 0,
  campaignRequirements: (state) =>
    Object.entries(state.campaign.deliverables).filter(([, deliverableData]) => deliverableData.enabled),
  requiredContentCount(state) {
    // youtube_video doesn't require upload
    const { youtube_video, ...deliverables } = state.campaign.deliverables;

    return Object.entries(deliverables)
      .filter(([, deliverableData]) => deliverableData.enabled)
      .map(([, deliverableData]) => deliverableData.count)
      .reduce((carry, count) => carry + count);
  },
  instagramMedia: (state) =>
    state.postedDeliverables.instagramPosts
      ? state.postedDeliverables.instagramPosts.filter((post) => post.media_product_type === 'FEED')
      : null,
  instagramStories: (state) =>
    state.postedDeliverables.instagramPosts
      ? state.postedDeliverables.instagramPosts.filter((post) => post.media_product_type === 'STORY')
      : null,
  instagramReels: (state) =>
    state.postedDeliverables.instagramPosts
      ? state.postedDeliverables.instagramPosts.filter((post) => post.media_product_type === 'REELS')
      : null,
  instagramIgtv: (state) =>
    state.postedDeliverables.instagramPosts
      ? state.postedDeliverables.instagramPosts.filter((post) => post.media_product_type === 'FEED')
      : null,
  failedInstagramMedia: (state) =>
    state.failedDeliverables.instagramPosts
      ? state.failedDeliverables.instagramPosts.filter((post) => post.media_product_type === 'FEED')
      : null,
  failedInstagramReels: (state) =>
    state.failedDeliverables.instagramPosts
      ? state.failedDeliverables.instagramPosts.filter((post) => post.media_product_type === 'REELS')
      : null,
  tikTokPosts: (state) => state.postedDeliverables.tikTokPosts,
  youtubeShorts: (state) =>
    state.postedDeliverables.youtubePosts
      ? state.postedDeliverables.youtubePosts.filter((post) => post.type === 'shorts')
      : null,
  hasApprovedContent: (state) =>
    state.campaignApplication &&
    ['publish_content', 'pending_payment', 'completed'].includes(state.campaignApplication.status),
  posted: (state) => state.campaignApplication?.status === state.campaignApplicationStatuses.completed,
  orderPlaced: (state) => state.order && !isUndefined(state.order.id),
  orderShipped: (state) => state.order && state.order.id && state.order.vendor_shipment_status === 'shipped',
  orderDelivered: (state) => (state.campaignApplication ? !isNull(state.order.order_delivered_at) : false),
  deliverablesLeftToPost(state, getters) {
    const deliverables = [];

    getters.campaignRequirements.forEach(([deliverable]) => {
      switch (deliverable) {
        case 'youtube_video':
          if (!state.postedDeliverables.youtubeLink) deliverables.push('Youtube video');
          break;
        case 'youtube_shorts':
          if (!getters.hasPostedDeliverable('youtube_shorts', getters.youtubeShorts)) {
            deliverables.push('Youtube short');
          }
          break;
        case 'tiktok_video':
          if (!getters.hasPostedDeliverable('tiktok_video', getters.tikTokPosts)) deliverables.push('Tiktok video');
          break;
        case 'reels':
          if (!getters.hasPostedDeliverable('reels', getters.instagramReels)) deliverables.push('Reels');
          break;
        case 'instagram_post':
          if (!getters.hasPostedDeliverable('instagram_post', getters.instagramMedia)) {
            deliverables.push('Instagram post');
          }
          break;
        case 'instagram_story':
          if (!getters.hasPostedDeliverable('instagram_story', getters.instagramStories)) {
            deliverables.push('Instagram story');
          }
          break;
        case 'igtv':
          if (!getters.hasPostedDeliverable('igtv', getters.instagramIgtv)) deliverables.push('IGTV');
          break;
        default:
          break;
      }
    });

    return deliverables;
  },
  campaignShouldDeliver: (state) => (deliverable) =>
    state.campaign && state.campaign.deliverables ? state.campaign.deliverables[deliverable].enabled : false,
  campaignHasCreativeAssetsObjective(state) {
    return state.campaign.objective === 'creative_assets';
  },
  isPaidCampaign(_state, getters) {
    return getters.isPaidFlatRateCampaign || getters.isPaidDynamicRateCampaign;
  },
  isPaidFlatRateCampaign(state) {
    return state.campaign.paid === 'flat_rate';
  },
  isPaidDynamicRateCampaign(state) {
    return state.campaign.paid === 'dynamic_rate';
  },
  hasPayment(state) {
    if (state.campaignApplication) {
      return !isUndefined(state.campaignApplication.payment) && !isNull(state.campaignApplication.payment);
    }

    return false;
  },
  isNegotiating(state) {
    return state.campaignApplication && state.campaignApplication.status === 'negotiating';
  },
  isPendingContract(state) {
    return state.campaignApplication && state.campaignApplication.status === 'pending_contract';
  },
  hasInstagramDeliverables: (state, getters) => () => {
    return (
      getters.campaignShouldDeliver('instagram_post') ||
      getters.campaignShouldDeliver('instagram_story') ||
      getters.campaignShouldDeliver('reels') ||
      getters.campaignShouldDeliver('igtv')
    );
  },
  hasYoutubeDeliverables: (state, getters) => () => {
    return getters.campaignShouldDeliver('youtube_video') || getters.campaignShouldDeliver('youtube_shorts');
  },
  hasTiktokDeliverables: (state, getters) => () => {
    return getters.campaignShouldDeliver('tiktok_video');
  },
  hasPostedDeliverable: (state) => (deliverableName, data) =>
    state.campaign && state.campaign.deliverables && data
      ? state.campaign.deliverables[deliverableName].count === data.length
      : false,
  UTMLink(state, getters) {
    let utmLink = false;

    if (getters.hasInstagramDeliverables()) {
      if (state.creator.affiliate) {
        utmLink = state.creator.affiliate.short_url;
      } else {
        utmLink = `${
          state.campaign.utm_url.split('?utm')[0]
        }?utm_source=instagram&utm_medium=creators&utm_campaign=${encodeURIComponent(state.creator.instagram_handle)}`;
      }
    }

    if (getters.hasYoutubeDeliverables()) {
      if (state.creator.affiliate) {
        utmLink = state.creator.affiliate.short_url;
      } else {
        utmLink = `${
          state.campaign.utm_url.split('?utm')[0]
        }?utm_source=youtube&utm_medium=creators&utm_campaign=${encodeURIComponent(state.creator.instagram_handle)}`;
      }
    }

    if (getters.hasTiktokDeliverables()) {
      if (state.creator.affiliate) {
        utmLink = state.creator.affiliate.short_url;
      } else {
        utmLink = `${
          state.campaign.utm_url.split('?utm')[0]
        }?utm_source=tiktok&utm_medium=creators&utm_campaign=${encodeURIComponent(state.creator.instagram_handle)}`;
      }
    }

    return utmLink;
  },
};

const actions = {};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
